import { type ColumnDef } from "@tanstack/react-table";
import { IconPackage } from "@tabler/icons-react";

import { DataTable } from "@/components/ui/data-table";
import type { DashboardProduct, StatValue } from "@/types/dashboard";
import { formatAmount, formatNumber } from "@/utils/formatters";
import { DashboardCard } from "@/components/dashboard/dashboard-card";

const columns: ColumnDef<DashboardProduct>[] = [
  {
    accessorKey: "title",
    header: "Product",
    cell: ({ row }) => (
      <span className="flex min-w-0 items-center gap-2.5">
        <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded bg-muted text-muted-foreground">
          {row.original.image ? (
            <img src={row.original.image} alt="" className="h-full w-full object-cover" />
          ) : (
            <IconPackage size={14} stroke={1.75} />
          )}
        </span>
        <span className="block max-w-[12rem] truncate font-medium text-foreground" title={row.original.title}>
          {row.original.title}
        </span>
      </span>
    ),
  },
  {
    accessorKey: "units",
    header: "Units",
    meta: { fit: true },
    cell: ({ row }) => <span className="tabular-nums">{formatNumber(row.original.units)}</span>,
  },
  {
    accessorKey: "affiliates",
    header: "Affiliates",
    meta: { fit: true },
    cell: ({ row }) => <span className="tabular-nums">{formatNumber(row.original.affiliates)}</span>,
  },
  {
    accessorKey: "revenue",
    header: "Revenue",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="tabular-nums font-medium text-foreground">{formatAmount(row.original.revenue)}</span>
    ),
  },
];

export function TopProductsCard({
  data,
  stats,
  isLoading,
}: {
  data: DashboardProduct[] | undefined;
  stats?: StatValue[];
  isLoading: boolean;
}) {
  return (
    <DashboardCard
      id="dashboard-top-products-card"
      title="Top products"
      to="/brand/products/list"
      stats={stats}
      bodyClassName="px-3 pb-3"
    >
      {/* Same five-row body as the affiliates card beside it. */}
      <div className="min-h-[14.5rem]">
        <DataTable
          columns={columns}
          data={(data ?? []).slice(0, 5)}
          isLoading={isLoading}
          skeletonRows={5}
          getRowId={(row) => row.productId}
          emptyText="No product sales in this period"
          minTableWidth="min-w-0"
        />
      </div>
    </DashboardCard>
  );
}
